import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import {PRICE_ITEM_RELS_DATA} from "@/modules/ClientPriceElementForm/_store";

const PRODUCT_CONTAINER_DECIMAL_PRECISION = 3;

const normalizeProductContainerDecimal = (value: any): string | null => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
        return null;
    }

    return numericValue.toFixed(PRODUCT_CONTAINER_DECIMAL_PRECISION);
};

interface CounterpartyFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    container_rels: any[];
    targetForDelete: any[];
    form: any;
    stickers: any[];
    schema: any;
    primeCostForm: any;
    primeCostSchema: any;
    productName: string | null;
    baseContainerName: string | null;

    meta: any;
}

const emptyPrimeCostForm = (containerId?: number | string | null) => ({
    id: null,
    cost: null,
    trade_price: null,
    retail_price: null,
    month: moment().format('YYYY-MM-01'),
    product_container_id: containerId || null
});

const state: CounterpartyFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    container_rels: [],
    targetForDelete: [],
    form: null,
    stickers: [],
    schema: null,
    primeCostForm: emptyPrimeCostForm(),
    primeCostSchema: null,
    productName: null,
    baseContainerName: null,

    meta: {},
};

const initState: CounterpartyFormState = cloneDeep(state);

export const PRODUCT_CONTAINER_DATA = 'GET_PRODUCT_CONTAINER_DATA';
export const PRODUCT_CONTAINER_STICKER_DATA = 'GET_PRODUCT_CONTAINER_STICKER_DATA';
export const PRODUCT_CONTAINER_SCHEMA = 'GET_PRODUCT_CONTAINER_SCHEMA';
export const PRIME_COST_DATA = 'GET_CONTAINER_PRIME_COST';
export const PRIME_COST_SCHEMA = 'GET_PRIME_COST_SCHEMA';

async function loadPrimeCostForContainer(vm: any, containerId: number | string) {
    const month = moment().format('YYYY-MM-01');

    const result = await vm.$db([
        vm.$q(PRIME_COST_DATA)
            .raw('documents.list')
            .action({
                columns: {
                    id: 'prime_costs.id',
                    cost: 'prime_costs.cost',
                    trade_price: 'prime_costs.trade_price',
                    retail_price: 'prime_costs.retail_price',
                    month: 'prime_costs.month',
                    product_container_id: 'prime_costs.product_container_id'
                },
                from: 's:prime_costs',
                from_as: 'prime_costs',
                where: {
                    $and: [
                        { $eq: ['prime_costs.product_container_id', { $: containerId }] },
                        { $eq: ['prime_costs.month', { $: month }] }
                    ]
                },
                page_size: 1
            }),
        vm.$q(PRIME_COST_SCHEMA)
            .raw('schemas.get')
            .action({ id: 's:prime_costs' })
    ]);

    const documents = result.getOperation(PRIME_COST_DATA).getContent().documents || [];
    const schema = result.getOperation(PRIME_COST_SCHEMA).getContent().schema;

    console.debug('[ProductContainerForm] prime_costs', {
        containerId,
        month,
        found: documents.length > 0
    });

    if (documents.length > 0) {
        return {
            primeCostForm: generateFormObject(schema, documents[0]),
            primeCostSchema: schema
        };
    }

    return {
        primeCostForm: emptyPrimeCostForm(containerId),
        primeCostSchema: schema
    };
}

async function savePrimeCostForContainer(vm: any, state: CounterpartyFormState, containerId: number | string) {
    if (!state.primeCostSchema) {
        return;
    }

    const form = {
        ...state.primeCostForm,
        product_container_id: containerId,
        month: moment().format('YYYY-MM-01')
    };

    const normalized = prepareFormObjectForSave(state.primeCostSchema, form);
    const hasValues =
        normalized.cost != null ||
        normalized.trade_price != null ||
        normalized.retail_price != null;

    if (!hasValues && !normalized.id) {
        return;
    }

    console.debug('[ProductContainerForm] save prime_costs', {
        cost: normalized.cost,
        trade_price: normalized.trade_price,
        retail_price: normalized.retail_price
    });

    const saveQuery = normalized.id
        ? vm.$q('PRIME_COST_SAVE').update(normalized.id, normalized)
        : vm.$q('PRIME_COST_SAVE').create('s:prime_costs', normalized);

    await vm.$db([saveQuery]);
}

export const actions: ActionTree<CounterpartyFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit, dispatch }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRODUCT_CONTAINER_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
                vm.$q(PRODUCT_CONTAINER_STICKER_DATA)
                    .raw('documents.list')
                    .action({
                    columns: {
                        id: 'pcs.id',
                        sticker_id: 'pcs.sticker_id',
                        name: 'vs.name'
                    },
                    from: `s:product_containers_stickers`,
                    from_as: 'pcs',
                    joins: [
                        {
                            join: 's:voc_stickers',
                            as: 'vs',
                            on: {
                                $eq: ['vs.id', 'pcs.sticker_id']
                            }
                        },
                    ],
                    where: {
                        $or: [
                            { $eq: [`pcs.product_container_id`, { $: params.id }] },
                        ]
                    }
                    }),
            ]).then(async (result: ServerResult) => {
                const primeCostData = await loadPrimeCostForContainer(vm, params.id);
                const containerData = result.getOperation(PRODUCT_CONTAINER_DATA).getContent();

                commit('SET_FORM_DATA', {
                    ...containerData,
                    stickers: result.getOperation(PRODUCT_CONTAINER_STICKER_DATA).getContent().documents,
                    ...primeCostData
                });

                const document = containerData.document || {};
                if (document.product_id) {
                    dispatch('loadProductName', document.product_id);
                } else {
                    commit('SET_PRODUCT_NAME', null);
                }

                if (document.base_product_container_id) {
                    dispatch('loadBaseContainerName', document.base_product_container_id);
                } else {
                    commit('SET_BASE_CONTAINER_NAME', null);
                }

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    loadProductName({ commit }, productId) {
        const vm = (this as any)._vm;

        return vm.$db([
            vm.$q('LOAD_CONTAINER_PRODUCT_NAME')
                .raw('documents.get')
                .action({
                    schema: 'products',
                    id: productId,
                    columns: {
                        name: 'products.name'
                    }
                })
        ]).then((result: ServerResult) => {
            const content = result.getOperation('LOAD_CONTAINER_PRODUCT_NAME').getContent();
            const name = content.document && content.document.name ? content.document.name : null;
            commit('SET_PRODUCT_NAME', name);
            return name;
        }).catch((error: any) => {
            console.error('[ProductContainerForm.loadProductName] error', error);
            commit('SET_PRODUCT_NAME', null);
            return null;
        });
    },

    loadBaseContainerName({ commit }, containerId) {
        const vm = (this as any)._vm;

        return vm.$db([
            vm.$q('LOAD_BASE_CONTAINER_NAME')
                .raw('documents.get')
                .action({
                    schema: 'product_containers',
                    id: containerId,
                    columns: {
                        name: 'product_containers.name'
                    }
                })
        ]).then((result: ServerResult) => {
            const content = result.getOperation('LOAD_BASE_CONTAINER_NAME').getContent();
            const name = content.document && content.document.name ? content.document.name : null;
            commit('SET_BASE_CONTAINER_NAME', name);
            return name;
        }).catch((error: any) => {
            console.error('[ProductContainerForm.loadBaseContainerName] error', error);
            commit('SET_BASE_CONTAINER_NAME', null);
            return null;
        });
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRODUCT_CONTAINER_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:product_containers`
                    }),
                vm.$q(PRIME_COST_SCHEMA)
                    .raw('schemas.get')
                    .action({ id: 's:prime_costs' })
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(PRODUCT_CONTAINER_SCHEMA).getContent(),
                    initParams,
                    primeCostSchema: result.getOperation(PRIME_COST_SCHEMA).getContent().schema,
                    primeCostForm: emptyPrimeCostForm(initParams && initParams.product_id ? null : null)
                });

                commit('LOADED_SUCCESS');
                resolve(undefined);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);
        const originalWeight = state.form && state.form.weight;
        const originalVolume = state.form && state.form.volume;

        // Сброс формата штрихкода («Не задан» = NULL) обязан доехать до UPDATE:
        // prepareFormObjectForSave вырезает null-поля, а DocAPI-update частичный —
        // без явного null в payload старое значение формата осталось бы в БД.
        if (state.form.barcode_format === null || state.form.barcode_format === '') {
            normalizedForm.barcode_format = null;
        }

        const normalizedWeight = normalizeProductContainerDecimal(originalWeight);
        const normalizedVolume = normalizeProductContainerDecimal(originalVolume);

        console.debug('[ProductContainerForm/saveForm] scoped weight/volume precision flow started', {
            scopedPrecisionChange: true,
            schemaCode: state.schema && state.schema.code,
            recordId: documentId || null,
            originalWeight,
            originalVolume,
            defaultSerializedWeight: normalizedForm && normalizedForm.weight,
            defaultSerializedVolume: normalizedForm && normalizedForm.volume,
            targetPrecision: PRODUCT_CONTAINER_DECIMAL_PRECISION
        });

        if (normalizedWeight !== null) {
            normalizedForm.weight = normalizedWeight;
        }

        if (normalizedVolume !== null) {
            normalizedForm.volume = normalizedVolume;
        }

        console.debug('[ProductContainerForm/saveForm] weight/volume precision normalized', {
            scopedPrecisionChange: true,
            recordId: documentId || null,
            finalSerializedWeight: normalizedForm && normalizedForm.weight,
            finalSerializedVolume: normalizedForm && normalizedForm.volume,
            targetPrecision: PRODUCT_CONTAINER_DECIMAL_PRECISION
        });

        const stickers = state.stickers.map((item: any) => {
            return {
                id: item.id,
                sticker_id: item.sticker_id
            }
        });

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('PRODUCT_CONTAINER_DATA_SAVE')
                    .update(documentId, normalizedForm)
                    .createManyRelation('product_containers_stickers', 'product_container_id', (createRelated: any) => {
                        console.log(stickers)
                      return stickers.filter((item: any) => !item.id || (typeof (item.id) !== 'string')).map((item, index) => createRelated(item));
                    })
                    .deleteManyRelation('product_containers_stickers', 'product_container_id', (deleteRelated: any) => {
                      return state.targetForDelete.map((item: any, index) => deleteRelated({id: item}));
                    })
            } else {
                saveQuery = vm.$q('PRODUCT_CONTAINER_DATA_SAVE')
                    .create('s:product_containers', normalizedForm)
                    .withManyRelation('product_containers_stickers', 'product_container_id', (createRelated: any) => {
                      return stickers.map((item: any) => createRelated(item));
                    })
            }

            vm.$db([
                saveQuery
            ]).then(async (result: ServerResult) => {
                const operationResult = result.getOperation('PRODUCT_CONTAINER_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.debug('[ProductContainerForm/saveForm] save failed', {
                        scopedPrecisionChange: true,
                        recordId: documentId || null,
                        serializedWeight: normalizedForm && normalizedForm.weight,
                        serializedVolume: normalizedForm && normalizedForm.volume,
                        operationError: operationResult.getContent()
                    });
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    const savedDocument = operationResult.getContent().document;
                    const savedContainerId = documentId || savedDocument.id;

                    try {
                        await savePrimeCostForContainer(vm, state, savedContainerId);
                    } catch (primeCostError) {
                        console.error('[ProductContainerForm/saveForm] prime cost save failed', primeCostError);
                    }

                    console.debug('[ProductContainerForm/saveForm] save succeeded', {
                        scopedPrecisionChange: true,
                        recordId: documentId || null,
                        serializedWeight: normalizedForm && normalizedForm.weight,
                        serializedVolume: normalizedForm && normalizedForm.volume
                    });
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.debug('[ProductContainerForm/saveForm] save request rejected', {
                    scopedPrecisionChange: true,
                    recordId: documentId || null,
                    serializedWeight: normalizedForm && normalizedForm.weight,
                    serializedVolume: normalizedForm && normalizedForm.volume,
                    error
                });
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit, dispatch}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});

        if (field === 'product_id') {
            if (newValue) {
                dispatch('loadProductName', newValue);
            } else {
                commit('SET_PRODUCT_NAME', null);
            }
        }

        if (field === 'base_product_container_id') {
            if (newValue) {
                dispatch('loadBaseContainerName', newValue);
            } else {
                commit('SET_BASE_CONTAINER_NAME', null);
            }
        }
    },

    changePrimeCostValue({ commit }, { field, newValue }) {
        commit('CHANGE_PRIME_COST_VALUE', { field, newValue });
    },

    addSticker({ commit }, sticker) {
      commit('ADD_STICKER', sticker);
    },

    removeSticker({ commit }, index: number) {
      commit('REMOVE_STICKER', index);
    },

};

export const mutations: MutationTree<CounterpartyFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {document, schema, stickers, primeCostForm, primeCostSchema}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
        state.stickers = stickers;
        state.primeCostForm = primeCostForm || emptyPrimeCostForm(document && document.id);
        state.primeCostSchema = primeCostSchema || null;
    },

    SET_PRODUCT_NAME(state, name) {
        state.productName = name;
    },

    SET_BASE_CONTAINER_NAME(state, name) {
        state.baseContainerName = name;
    },

    GENERATE_FORM_DATA(state, {schema, initParams, primeCostForm, primeCostSchema}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
        state.primeCostForm = primeCostForm || emptyPrimeCostForm();
        state.primeCostSchema = primeCostSchema || null;
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;
        
        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: CounterpartyFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },

    CHANGE_PRIME_COST_VALUE(state: CounterpartyFormState, { field, newValue }) {
        state.primeCostForm[field] = newValue;
    },

    ADD_STICKER(state, sticker) {
      state.stickers.push({
        sticker_id: sticker.id,
        name: sticker.label
      })
    },

    REMOVE_STICKER(state, index) {
      if (state.stickers[index].id) {
        state.targetForDelete.push(state.stickers[index].id)
      }

      state.stickers.splice(index, 1);
    }
};

const getters = {
    fields: (state: CounterpartyFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_product_catalog_product_container_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
